import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/db6gf1q2sgkaik1unf00';

const highlights = [
  {
    icon: 'ri-stack-line',
    title: '多类别内容聚合',
    desc: '覆盖电影、电视剧、综艺与短视频四大类别，满足不同观看偏好。',
  },
  {
    icon: 'ri-filter-3-line',
    title: '多维筛选发现',
    desc: '按地区、年份与热度排序快速筛出你感兴趣的内容，浏览更高效。',
  },
  {
    icon: 'ri-search-eye-line',
    title: '便捷搜索体验',
    desc: '关键词模糊搜索，配合分类标签与相关推荐，轻松找到想看的内容。',
  },
  {
    icon: 'ri-device-line',
    title: '多端自适应',
    desc: '手机、平板与桌面端自适应布局，随时随地舒适浏览。',
  },
];

export default function About() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setSuccessMsg('提交成功，感谢你的留言，我们会尽快回复。');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') params.append(key, value);
    });

    setStatus('loading');
    setFormError('');
    setSuccessMsg('');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK';

      if (isOk && !isSpam) {
        setStatus('success');
        setSuccessMsg('提交成功，感谢你的留言，我们会尽快回复。');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg
            ? serverMsg
            : '提交失败，请稍后重试或通过邮箱联系我们。'
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试或通过邮箱联系我们。');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* 头部 */}
        <section className="relative w-full overflow-hidden bg-foreground-950">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20cinematic%20wide%20banner%20of%20warm%20amber%20light%20rays%20over%20a%20dark%20minimal%20gradient%20backdrop%20with%20soft%20floating%20particles%2C%20deep%20charcoal%20and%20orange%20tones%2C%20volumetric%20soft%20lighting%2C%20atmospheric%20depth%2C%20high%20detail%2C%20elegant%20modern%20composition%2C%20no%20text&width=1600&height=520&seq=tx-about-hero&orientation=landscape"
            alt="糖心视频平台介绍背景"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/70 to-foreground-950/50"></div>
          <div className="relative z-10 max-w-[1440px] mx-auto px-4 md:px-8 py-12 md:py-16">
            <nav className="flex items-center gap-2 text-sm text-background-200/80 mb-5 flex-wrap">
              <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-background-50">关于我们</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-background-50">
              关于糖心视频
            </h1>
            <p className="mt-4 text-sm md:text-base text-background-200/85 max-w-2xl leading-relaxed">
              一个专注内容发现的高清视频聚合平台，让每一位观众都能轻松找到喜欢的作品。
            </p>
          </div>
        </section>

        <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-10">
          {/* 站点定位 */}
          <section className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-10">
            <h2 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950 flex items-center gap-2">
              <span className="w-1 h-6 rounded-full bg-primary-500"></span>
              站点定位
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed select-text">
              糖心视频致力于为广大视频爱好者提供一个涉及电影、电视剧、综艺及短视频等多类别高清视频内容的聚合平台。
              我们关注内容的丰富性与发现的便捷性，通过清晰的内容层级、多维筛选与相关推荐，帮助用户快速了解并进入感兴趣的作品。
              本站当前为内容展示与发现平台的演示版本，页面内容均为虚构示例，真实视频播放接口暂未接入。
            </p>
          </section>

          {/* 我们的特点 */}
          <section className="mt-10">
            <h2 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950 flex items-center gap-2 mb-6">
              <span className="w-1 h-6 rounded-full bg-accent-500"></span>
              我们提供什么
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {highlights.map((item) => (
                <div
                  key={item.title}
                  className="p-5 rounded-lg bg-background-50 border border-background-200"
                >
                  <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 使用说明 + 联系方式 */}
          <section className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
              <h2 className="font-heading text-xl font-extrabold text-foreground-950 flex items-center gap-2 mb-5">
                <span className="w-1 h-6 rounded-full bg-primary-500"></span>
                使用说明
              </h2>
              <ol className="flex flex-col gap-4">
                {[
                  '从首页的分类快捷入口进入你感兴趣的频道，或在顶部导航选择分类。',
                  '在分类页使用“地区 / 年份 / 排序”筛选，快速定位心仪内容。',
                  '点击任意视频卡片进入详情页，查看作品信息与相关推荐。',
                  '使用顶部搜索图标或搜索页，输入关键词查找想看的内容。',
                ].map((step, i) => (
                  <li key={step} className="flex gap-3">
                    <span className="w-7 h-7 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-bold">
                      {i + 1}
                    </span>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed pt-0.5">{step}</p>
                  </li>
                ))}
              </ol>

              <div className="mt-8 pt-6 border-t border-background-200">
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-4">联系方式</h3>
                <div className="flex flex-col gap-3 text-sm">
                  <p className="flex items-center gap-3 text-foreground-600">
                    <i className="ri-mail-line text-primary-500 text-lg"></i>
                    联系邮箱：contact@tangxin-video.example
                  </p>
                  <p className="flex items-center gap-3 text-foreground-600">
                    <i className="ri-customer-service-2-line text-primary-500 text-lg"></i>
                    工作时间：周一至周五 9:00 - 18:00
                  </p>
                </div>
              </div>
            </div>

            {/* 联系表单 */}
            <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
              <h2 className="font-heading text-xl font-extrabold text-foreground-950 flex items-center gap-2 mb-2">
                <span className="w-1 h-6 rounded-full bg-accent-500"></span>
                留言给我们
              </h2>
              <p className="text-sm text-foreground-500 mb-5">
                有任何建议或合作意向，欢迎填写下方表单，我们会尽快与你联系。
              </p>

              <form onSubmit={handleSubmit} data-readdy-form={FORM_URL} className="flex flex-col gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    姓名
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的姓名"
                    className="w-full h-11 px-3 rounded-md bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none border border-transparent focus:border-primary-400 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="请输入你的邮箱"
                    className="w-full h-11 px-3 rounded-md bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none border border-transparent focus:border-primary-400 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你的问题或建议（最多 500 字）"
                    className="w-full px-3 py-2.5 rounded-md bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none border border-transparent focus:border-primary-400 transition-colors resize-none"
                  ></textarea>
                </div>

                <input
                  className="anti-spam-field"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? '提交中…' : '提交留言'}
                </button>

                {status === 'success' && (
                  <p className="flex items-center gap-2 text-sm text-primary-600">
                    <i className="ri-checkbox-circle-line"></i>
                    {successMsg}
                  </p>
                )}
                {status === 'error' && (
                  <p className="flex items-center gap-2 text-sm text-accent-700">
                    <i className="ri-error-warning-line"></i>
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}